import React, { useCallback, useEffect, useRef, useState } from 'react';
import Drawer from 'devextreme-react/drawer';
import ScrollView from 'devextreme-react/scroll-view';
import { Outlet } from 'react-router-dom';
import {
  Header, Footer, SideNavigationMenu
} from '../../components';
import { useScreenSize } from '../../utils/media-query';
import './side-nav-inner-toolbar.scss';

// Signed-in layout: purple side menu (Figma "Mobile Side Menu") beside the page.
// Large screens keep the menu open beside the content; smaller screens collapse it
// to an icon rail (or hide it entirely on phones) and overlay it when opened.
export default function ({ title, editProfileInfo }) {
  const scrollViewRef = useRef(null);
  const { isXSmall, isLarge } = useScreenSize();
  const [menuOpened, setMenuOpened] = useState(isLarge);
  const [temporaryMenuOpened, setTemporaryMenuOpened] = useState(false);

  useEffect(() => {
    setMenuOpened(isLarge);
    setTemporaryMenuOpened(false);
  }, [isLarge]);

  const shaderEnabled = !isLarge;

  const toggleMenu = useCallback(() => {
    setMenuOpened(opened => !opened);
    setTemporaryMenuOpened(false);
  }, []);

  const openMenu = useCallback(() => {
    setMenuOpened(true);
    setTemporaryMenuOpened(true);
  }, []);

  const onNavigate = useCallback(() => {
    if (!isLarge || temporaryMenuOpened) {
      setMenuOpened(false);
      setTemporaryMenuOpened(false);
    }
  }, [isLarge, temporaryMenuOpened]);

  // The app icon in the menu toggles it. On the large layout the menu stays put
  // after navigating; elsewhere an opened menu is temporary and closes again.
  const onLogoClick = useCallback(() => {
    if (menuOpened || isLarge) {
      toggleMenu();
    } else {
      openMenu();
    }
  }, [menuOpened, isLarge, toggleMenu, openMenu]);

  const closeDrawer = useCallback(e => {
    // The header's menu button lives in the Drawer's view area, so its click also
    // reaches closeOnOutsideClick and would immediately undo the toggle.
    if (!shaderEnabled || e?.target?.closest?.('.menu-button')) {
      return false;
    }
    setMenuOpened(false);
    setTemporaryMenuOpened(false);
    return true;
  }, [shaderEnabled]);

  return (
    <div className="side-nav-inner-toolbar">
      <Drawer
        className="layout-body"
        position="before"
        opened={menuOpened}
        openedStateMode={isLarge ? 'shrink' : 'overlap'}
        revealMode={isXSmall ? 'slide' : 'expand'}
        minSize={isXSmall ? 0 : 60}
        maxSize={250}
        shading={shaderEnabled}
        closeOnOutsideClick={closeDrawer}
        render={() => (
          <SideNavigationMenu
            compactMode={!menuOpened}
            openMenu={openMenu}
            onLogoClick={onLogoClick}
            onNavigate={onNavigate}
          />
        )}
      >
        <div className="layout-view">
          <Header
            title={title}
            editProfileInfo={editProfileInfo}
            menuToggleEnabled={isXSmall}
            toggleMenu={toggleMenu}
            signOutVisible={false}
          />
          <ScrollView
            className="layout-scroll"
            useNative={true}
            height="100%"
            width="100%"
            ref={scrollViewRef}
          >
            <div className="content">
              <Outlet />
            </div>
          </ScrollView>
          <Footer
            scrollViewRef={scrollViewRef}
          />
        </div>
      </Drawer>
    </div>
  );
}
